import {
  useState, useEffect, useRef, useCallback, useContext,
} from 'react';
import { useLazyQuery } from '@apollo/client';
import WithSignIn from 'components/withSignIn';
import Filters from 'components/filters';
import { useToastOnEffect } from 'utils/customHooks';
import { useNameAndDate, getNameDate } from 'utils/filters';
import UserContext from 'utils/context';
import { GET_POOLS } from 'lib/api';

import Pools from 'components/pools';

const limitVal = 25;

const PoolsPage = () => {
  const { getEvtSourceConOrOpen, updatedSse, user } = useContext(UserContext);
  const containerRef = useRef();

  const [dataStore, setDataStore] = useState([]);
  const [noMoreResult, setNoMoreResult] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const isSourceFine = getEvtSourceConOrOpen();
  const [pagination, setPagination] = useState({
    offset: 0,
    limit: limitVal,
  });
  const [getPools, {
    loading, error, data = {}, fetchMore = () => {},
  }] = useLazyQuery(GET_POOLS);
  const {
    ticketName, curDate, onTicketChange, setCurDate, reset,
  } = useNameAndDate();

  useToastOnEffect('Something went wrong 😔. Please reload page.', [error], 'error');

  const loadMore = () => {
    const newPagination = {
      offset: pagination.offset + limitVal,
      limit: limitVal,
    };
    fetchMore({
      variables: newPagination,
      updateQuery: (prev, { fetchMoreResult }) => {
        const newResult = fetchMoreResult?.findPools;
        if (!newResult?.length) {
          setNoMoreResult(true);
          return prev;
        }
        setNoMoreResult(false);
        const res = {
          findPools: [...prev.findPools, ...newResult],
        };
        return res;
      },
    });
    setPagination(newPagination);
  };

  useEffect(() => {
    if (data?.findPools && !refreshing) {
      setDataStore(data.findPools);
    }
  }, [data?.findPools, refreshing]);

  useEffect(() => {
    const { from, to, name } = getNameDate(ticketName, curDate);
    const getLinesWithFill = async () => {
      await getPools({
        variables: {
          offset: 0,
          limit: limitVal,
          name,
          from,
          to,
        },
      });
      setNoMoreResult(false);
      setPagination({ offset: 0, limit: limitVal });
    };

    if (user?.nickname) {
      getLinesWithFill();
    }
  }, [ticketName, curDate, user?.nickname]);

  useEffect(() => {
    let observer;
    const elem = containerRef && containerRef.current;

    if (elem && data?.findPools?.length > 0) {
      observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !noMoreResult && !loading) {
            loadMore();
          }
        });
      }, { rootMargin: '20% 0px' });
      observer.observe(elem);
    }

    return () => observer?.disconnect();
  }, [containerRef, data?.findPools, noMoreResult, loading]);

  const refetch = useCallback(async () => {
    setRefreshing(true);
    const { from, to, name } = getNameDate(ticketName, curDate);
    await getPools({
      variables: {
        offset: 0,
        limit: pagination.offset + limitVal,
        name,
        from,
        to,
      },
    });
    setRefreshing(false);
  }, [curDate, ticketName, pagination]);

  useEffect(() => {
    const intervalMs = isSourceFine ? null : setInterval(refetch, 60000);
    return () => clearInterval(intervalMs);
  }, [refetch, isSourceFine]);

  useEffect(() => {
    if (updatedSse) {
      refetch();
    }
  }, [refetch, updatedSse]);

  return (
    <WithSignIn>
      <Filters
        onTicketChange={onTicketChange}
        setCurDate={setCurDate}
        curDate={curDate}
        reset={reset}
      />
      <Pools
        loading={loading}
        list={dataStore}
      />
      <div ref={containerRef} />
    </WithSignIn>
  );
};

export default PoolsPage;
